<template>
    <div class="page-default page--stats">
        <SidebarVue />
        <div class="content">
            <StatusBar :title="statusTitle" :showQuota="false" :showMenu="false" :showTpe="false" />
            <div class="content_inner">
                <div class="searchForm">
                    <FormKit
                        type="select"
                        name="sessionId"
                        placeholder="Choisissez une session de vente"
                        v-model="sessionId"
                        @change="updateSession"
                        :value="'all'"
                        :options="sessionsList"
                    />
                </div>
                <div class="main_infos">
                    <h3 class="title title--h3">Informations sur la session</h3>
                    <MTable
                        :selectable="false"
                        :show-labels="false"
                        :rows="sessionRows"
                        :labels="sessionLabels"
                    />
                    <div v-if="!sessionId" class="download-cash-out">
                        <Button @click="downloadCashOutSummary" :full-width="false">
                            Télécharger la synthèse des sorties de caisse
                        </Button>
                    </div>
                    <div v-else-if="selectedSession && selectedSession.status === 'closed'" class="download-cash-out">
                        <Button @click="downloadCashOut" :full-width="false">
                            Télécharger la sortie de caisse
                        </Button>
                        <Button v-if="isThermalPrinterEnabled" @click="printCashOutTicket" :full-width="false" variant="secondary">
                            Imprimer la sortie de caisse sur l'imprimante thermique
                        </Button>
                    </div>
                </div>

                <div class="synthese">
                    <h3 class="title title--h3">Synthèse des opérations</h3>
                    <MTable
                        class="table_ticket"
                        :selectable="false"
                        :show-labels="true"
                        :rows="syntheseTicketsRows"
                        :labels="syntheseTicketsLabel"
                    />
                </div>

                <div v-if="ticketTypesRows && ticketTypesRows.length > 0" class="ticket-types">
                    <h3 class="title title--h3">Détails par tarif</h3>
                    <MTable
                        :selectable="false"
                        :show-labels="true"
                        :rows="ticketTypesRows"
                        :labels="ticketTypesLabels"
                    />
                </div>

                <div class="recettes">
                    <h3 class="title title--h3">Recettes</h3>
                    <MTable
                        :selectable="false"
                        :show-labels="true"
                        :rows="recetteRows"
                        :labels="recetteLabels"
                    />
                </div>
                <div v-if="totalCaisse" class="solde">
                    <div class="details">Solde de caisse: <span>{{ totalCaisse }}</span></div>
                </div>

            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import SidebarVue from '@/components/organisms/Sidebar.vue';
import StatusBar from '@/components/organisms/StatusBar.vue';
import MTable from '@/components/molecules/Table.vue';
import Button from '@/components/atoms/Button.vue';

    import { SellingSession } from '@/interfaces/SellingSession';
import { useSellingSessionStore } from '@/stores/SellingSessionStore';
import { useUserStore } from '@/stores/UserStore';
import { onMounted, ref, computed, watch } from 'vue';
import { AppPrice } from '@/tools/Price';
import { PaymentMethodEnum, PaymentMethodTypeEnum } from '@/interfaces/Order';
import { useEventStore } from '@/stores/EventStore';
import { usePrintStore, PrintModeEnum } from '@/stores/PrintStore';

    const userStore = useUserStore()
    const sellingSessionStore = useSellingSessionStore()
    const eventStore = useEventStore()
    const printStore = usePrintStore()
    const sessionId = ref(null)
    const operatorId = ref(null)

    // Check if thermal printer is enabled
    const isThermalPrinterEnabled = computed(() => {
        return printStore.getPrinterMode === PrintModeEnum.OANDB_EPSON
    })

    onMounted(async () => {
        await sellingSessionStore.fetchAll()
        await fetchStats()
    })

    const statusTitle = computed(() => {
        if (eventStore.currentEvent) {
            return `${eventStore.currentEvent.name} - Statistiques`
        }
        return ''
    })

    const fetchStats = (async () => {
        await sellingSessionStore.fetchStats(sessionId.value)
    })

    watch(sessionId, async (oldValue, newValue) => {
        await fetchStats()
    })

    const selectedSession = computed<SellingSession|null>(() => {
        if (sessionId.value) {
            return sellingSessionStore.sellingSessions.find(sellingSession => sellingSession._id === sessionId.value)
        }
        if (sellingSessionStore.sellingSessions.length) {
            return sellingSessionStore.sellingSessions.find(sellingSession => sellingSession._id === userStore.sellingSession._id)
        }
        if ((operatorId.value && operatorId.value != userStore.me._id) && !sessionId.value) {
            return sellingSessionStore.sellingSessions.find(sellingSession => sellingSession.authorId === operatorId.value)
        }
        return null
    })

    const updateSession = (async () => {
        //await fetchData()
    })

    const updateOperator = (async () => {
        //await fetchData()
    })

    const sessionLabels = computed(() => {
        return [
            {
                label: "Label",
                value: "label",
                align: "left"
            },
            {
                label: "Value",
                value: "value",
                align: "right"
            },
        ]
    })

    const sessionRows = computed(() => {
        if (selectedSession.value) {
            return [
                { label: 'Opérateur', value: `${selectedSession.value.authorId?.firstName} ${selectedSession.value.authorId?.lastName}`},
                { label: 'Numéro', value: `${selectedSession.value.sessionNumber}`},
                { label: 'Ouverte le', value: `${formatDate(selectedSession.value.createdAt)}`},
                { label: 'Fermée le', value: (selectedSession.value.closedAt) ? formatDate(selectedSession.value.closedAt) : 'En cours...'}
            ]
        }
    })

    const syntheseTicketsLabel = computed(() => {
        return [
            { label: "Type de commande", value: "type", align: "left"},
            { label: "Commandes", value: "orders", align: "left"},
            { label: "Unités (Billets/Produits)", value: "units", align: "left"},
            { label: "Montant total", value: "price", align: "left"},
        ]
    })
    const syntheseTicketsRows = computed(() => {
        if (sellingSessionStore.stats) {
            return [
            {
                    type: 'Billetterie',
                    orders: sellingSessionStore.stats.ordersTicketingCount,
                    units: sellingSessionStore.stats.ticketsCount,
                    price: new AppPrice(sellingSessionStore.stats.ticketsTotalPrice).divide100(),
                },
                {
                    type: 'Buvette',
                    orders: sellingSessionStore.stats.ordersBuvetteCount,
                    units: sellingSessionStore.stats.productsCount,
                    price: new AppPrice(sellingSessionStore.stats.productsTotalPrice).divide100(),
                }
            ]
        }
    })

    const totalCaisse = computed(() => {
        if (sellingSessionStore.stats) {
            return new AppPrice(sellingSessionStore.stats.totalPrice).divide100()
        }
        return null
    })

    const ticketTypesLabels = computed(() => {
        return [
            { label: "Libellé", value: "label", align: "left"},
            { label: "Nombre de ventes", value: "quantity", align: "left"},
            { label: "Montant", value: "amount", align: "left"},
        ]
    })

    const ticketTypesRows = computed(() => {
        if (sellingSessionStore.stats && sellingSessionStore.stats.ticketTypes) {
            return sellingSessionStore.stats.ticketTypes.map(tt => ({
                label: tt.ticketTypeName,
                quantity: tt.ticketCount,
                amount: new AppPrice(tt.total).divide100()
            }))
        }
        return []
    })

    const recetteLabels = computed(() => {
        return [
            { label: "Type de paiement", value: "type", align: "left"},
            { label: "Montant total", value: "price", align: "left"},
        ]
    })

    const recetteRows = computed(() => {
        const rows = []
        if (sellingSessionStore.stats) {
            console.log(sellingSessionStore.stats);

            sellingSessionStore.stats.paymentMethods.forEach(paymentMethod => {
                if (paymentMethod.method !== PaymentMethodTypeEnum.free) {
                    let type = (PaymentMethodEnum[paymentMethod.method]) ? PaymentMethodEnum[paymentMethod.method] : paymentMethod.method
                    if (paymentMethod.method === PaymentMethodTypeEnum.custom && paymentMethod.customMethod) {
                        type = paymentMethod.customMethod.label
                    }
                    rows.push({
                        type,
                        total: new AppPrice(paymentMethod.total).divide100()
                    })
                }
            })
        }
        return rows
    })
    /**
     * @TODO : Refacto la partie date.
     */
     const formatDate = ((dateTime) => {
        let dateString = ''
        const date = new Date(dateTime)
        dateString += date.toLocaleDateString('fr', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
        dateString += ' à '
        dateString += date.toLocaleTimeString('fr', {
            hour: '2-digit',
            minute: '2-digit'
        })
        return dateString
    })

    const sessionsList = computed(() => {
        const sellingSessions = sellingSessionStore.sellingSessions.map(sellingSession => {
            let label = `${sellingSession.authorId?.firstName} ${sellingSession.authorId?.lastName} - ${formatDate(sellingSession.createdAt)}`
            if (sellingSession.status == 'opened') {
                label += ' (en cours)'
            }
            return {
                label,
                value: sellingSession._id
            }
        })
        return [
            {
                label: "Toutes les sessions",
                value: null
            },
            ...sellingSessions
        ]
    })

    const operatorsList = computed(() => {
        const operators = [
            {
                label: "Tous les opérateurs",
                value: null
            },
        ]
        sellingSessionStore.sellingSessions.forEach(sellingSession => {
            if (sellingSession.authorId && sellingSession.authorId) {
                const foundOperator = operators.find(operator => operator.value === sellingSession.authorId._id)
                if (!foundOperator) {
                    operators.push({
                        value: sellingSession.authorId._id,
                        label: `${sellingSession.authorId?.firstName} ${sellingSession.authorId?.lastName}`
                    })
                }
            }
        })

        return operators
    })

    const downloadCashOut = async () => {
        if (selectedSession.value && selectedSession.value._id) {
            await sellingSessionStore.downloadCashOut(selectedSession.value._id)
        }
    }

    const downloadCashOutSummary = async () => {
        await sellingSessionStore.downloadCashOutSummary()
    }

    const printCashOutTicket = async () => {
        if (selectedSession.value && selectedSession.value._id) {
            // Fetch stats for the session
            await sellingSessionStore.fetchStats(selectedSession.value._id)

            // Prepare print data
            const printData = sellingSessionStore.prepareCashOutPrintData(selectedSession.value)


            // Print
            await printStore.printCashOut(printData)
        }
    }

</script>

<style scoped>
.download-cash-out {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
</style>